import { DestroyRef, Injectable, computed, effect, inject, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { NavigationStart, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { L10nTranslationService } from 'angular-l10n';
import { Subscription, filter, finalize, switchMap, take } from 'rxjs';
import { selectSelectedTenantId } from '@wonder-core-ng/tenant-store/tenant.selectors';
import { isNotNull } from '@wonder-core-ng/utils';
import { AIApiService } from '../ai-store/ai.api.service';
import { AssistantPersona, BuilderAgent, ChatAgent, ChatMessage, ComposerAnswer, ComposerAnswers, GuideLink, OrchestratorResult, ProposalRevisionDraft, RouteHistoryTurn, RouteStreamEvent } from '../ai-store/ai-chat.types';
import { AlertCardState, AlertErrorKey } from '../ai-store/alert-card.types';
import { AlertApplyErrorCode } from '../ai-store/alert-proposal.types';
import { AIService } from '../ai-store/ai.service';
import { SessionService } from '@wonder-core-ng/session-store/session.service';
import { Permission } from '../interfaces/permission.enum';
import { DashboardProposalsService } from '../dashboard-config/dashboard-proposals.service';
import { AssistantAgentMode, AssistantScreen, AssistantScreenService } from './assistant-screen.service';

/** The screen that hosts each builder agent. */
const SCREEN_PATH: Record<AssistantAgentMode, string> = { query: '/query-config/create', chart: '/chart-config/create' };
/** The dashboard editor, where the dashboards the assistant composes are reviewed and saved. */
const DASHBOARD_EDITOR_PATH = '/dashboard-config/create';
/** What the chat says while the orchestrator has each agent at work. */
const STEP_KEYS: Record<string, string> = {
  find_in_dashboards: '$ai.global.step.find_in_dashboards',
  build_dashboard: '$ai.global.step.build_dashboard',
  revise_dashboard: '$ai.global.step.revise_dashboard',
  manage_alerts: '$ai.global.step.manage_alerts',
};
/** Agent at work behind each orchestrator step: its bubble wears that agent's face while it thinks. */
const STEP_AGENT: Record<string, ChatAgent> = { find_in_dashboards: 'guide', build_dashboard: 'composer', revise_dashboard: 'composer', manage_alerts: 'alerts' };
/** What the chat says while it takes the user to a builder. */
const GOING_TO: Record<BuilderAgent, string> = { query: '$ai.global.going-to.create_query', chart: '$ai.global.going-to.create_chart' };

/** Turns and characters per turn sent to the orchestrator: the server accepts 20 × 4000. */
const HISTORY_SENT = 10;
const HISTORY_TURN_CHARS = 4000;
/** The bubble shows the start of a reply, not the reply. */
const BUBBLE_CHARS = 120;
/** How long the bubble stays up; the unread marker stays until the panel is opened. */
export const BUBBLE_MS = 6000;
/**
 * How long a reply stays on screen before the assistant takes the user elsewhere on its own: long
 * enough to read what it answered. Links the user clicks open at once.
 */
export const REDIRECT_MS = 4000;

/**
 * The single assistant chat of the app (WB-155), opened from the header bar on every screen.
 *
 * On a builder screen (query or chart) it talks straight to that screen's agent, with the config the
 * screen is editing. Anywhere else the message goes to the orchestrator (WB-154), which answers in
 * place — running the guide agent on the server when the data may already be on a dashboard — or
 * hands the request to a builder: the user is taken to that agent's screen and the request is sent
 * once the screen is up. The conversation is the one `AIService` holds, so it survives the
 * navigation and the history works as before.
 */
@Injectable({ providedIn: 'root' })
export class GlobalAssistantService {
  private readonly api = inject(AIApiService);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly screens = inject(AssistantScreenService);
  private readonly translation = inject(L10nTranslationService);
  private readonly ai = inject(AIService);
  private readonly session = inject(SessionService);
  private readonly dashboardProposals = inject(DashboardProposalsService);
  private readonly canCreateDashboards = this.session.hasPermissions([Permission.DASHBOARD_CREATE]);
  /** Confirming an alert reads and saves a condition and an event: the server asks for read and update of both, so the card offers it only with all four. */
  readonly canManageAlerts = this.session.hasPermissions([Permission.CONDITION_READ, Permission.CONDITION_UPDATE, Permission.EVENT_READ, Permission.EVENT_UPDATE]);
  private readonly canCreate: Record<AssistantAgentMode, () => boolean> = {
    query: this.session.hasPermissions([Permission.QUERY_CREATE]),
    chart: this.session.hasPermissions([Permission.CHART_CREATE]),
  };
  /**
   * Without chart edit rights the assistant is only the guide: every message goes to the
   * orchestrator, which answers it with the guide, and nothing ever takes the user to a builder
   * form. The server enforces the same rule; this keeps the client from trying.
   */
  private readonly guideOnly = () => !this.canCreate.chart();

  private readonly _isOpen = signal(false);
  readonly isOpen = this._isOpen.asReadonly();
  /** Questionnaires of the composer already answered (WB-177 phase 5), by message id: never sent twice. */
  private readonly answeredQuestionnaires = signal<ReadonlySet<string>>(new Set());
  /**
   * Where each alert card stands (WB-222), by message id. Held here, not in the card, so it survives
   * the chat re-rendering; a card with no entry is pending.
   */
  private readonly alertCards = signal<Readonly<Record<string, AlertCardState>>>({});
  /** Waiting for the orchestrator; the builder agent's own turn is `AIService.loading`. */
  private readonly _routing = signal(false);
  readonly routing = this._routing.asReadonly();
  /** The orchestrator's reply as it is being written; replaced by the final reply when the turn ends. */
  private readonly _streamingReply = signal('');
  readonly streamingReply = this._streamingReply.asReadonly();
  /** The agent the orchestrator has at work, while there is no text to show yet. */
  private readonly _routeStep = signal<Extract<RouteStreamEvent, { type: 'step' }> | null>(null);
  /** What the thinking bubble says while the orchestrator works: which agent, and which chart the composer builds. */
  readonly routingStatus = computed<{ key: string; params: Record<string, unknown> }>(() => {
    const step = this._routeStep();
    if (!step) return { key: '$ai.global.thinking', params: {} };
    if (step.chart) return { key: '$ai.global.step.building_chart', params: { ...step.chart } };
    return { key: STEP_KEYS[step.tool] ?? '$ai.global.thinking', params: {} };
  });
  /** The agent the orchestrator has at work: the general one until it hands the turn to the guide or the composer. */
  readonly routingAgent = computed<ChatAgent>(() => {
    const step = this._routeStep();
    if (!step) return 'orchestrator';
    return step.chart ? 'composer' : (STEP_AGENT[step.tool] ?? 'orchestrator');
  });
  /** Something is being answered, by the orchestrator or a builder agent, or the chat is about to change screens. */
  readonly busy = () => this._routing() || this.ai.loading() || this._redirecting() !== null;
  /** The open builder screen, when there is one: the chat applies results through it. */
  readonly screen = this.screens.screen;

  /**
   * Which assistant the panel is showing: always the guide for a user who cannot edit charts; the
   * builder agent while a builder screen is open; the guide again after the general assistant
   * handed the last question to it; and the general one otherwise.
   */
  readonly persona = computed<AssistantPersona>(() => {
    if (this.guideOnly()) return 'guide';
    if (this.screens.screen()) return 'builder';
    const last = [...this.ai.messages()].reverse().find((m) => m.role === 'assistant' && !m.toolCall && !!m.content);
    return last?.answeredBy === 'guide' ? 'guide' : 'orchestrator';
  });

  /** A reply arrived while the panel was closed. */
  private readonly _unread = signal(false);
  readonly unread = this._unread.asReadonly();
  /** Start of that reply, shown for a few seconds next to the closed launcher. */
  private readonly _bubble = signal<string | null>(null);
  readonly bubble = this._bubble.asReadonly();
  private bubbleTimer: ReturnType<typeof setTimeout> | null = null;

  /** Id of the message that announces a change of screen, while the chat waits to make it. */
  private readonly _redirecting = signal<string | null>(null);
  readonly redirecting = this._redirecting.asReadonly();
  private redirectTimer: ReturnType<typeof setTimeout> | null = null;

  /** The orchestrator turn in flight, kept so it can be stopped; null when there is none. */
  private activeRoute: { subscription: Subscription | null; userTurn: ChatMessage; message: string; handBack: boolean } | null = null;
  /** Prompt of a turn the user stopped, handed back so it can be resent or reworded. */
  private readonly _cancelledMessage = signal<string | null>(null);
  readonly cancelledMessage = this._cancelledMessage.asReadonly();

  constructor() {
    // Anything that takes the user elsewhere while the assistant waits to do so is the user's newer
    // intent: the assistant's own move only starts once its timer is spent, so a navigation seen
    // while one is pending is never its own.
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        if (this._redirecting() !== null) this.cancelRedirect();
      });

    // A turn of the agent just ended: put its result where it belongs and tell the user if they
    // are not looking. The ids seen when the turn started tell this turn's messages apart.
    let wasLoading = false;
    let seenAtStart = new Set<string>();
    effect(() => {
      const loading = this.ai.loading();
      const started = !wasLoading && loading;
      const finished = wasLoading && !loading;
      wasLoading = loading;
      if (started) seenAtStart = new Set(untracked(() => this.ai.messages()).map((m) => m.id));
      if (finished) {
        untracked(() => {
          const turn = this.ai.messages().filter((m) => !seenAtStart.has(m.id));
          this.notifyIfClosed();
          this.takeResultToItsScreen(turn);
        });
      }
    });
    inject(DestroyRef).onDestroy(() => {
      this.clearBubbleTimer();
      this.clearRedirectTimer();
    });
  }

  open(): void {
    this._isOpen.set(true);
    this._unread.set(false);
    this.hideBubble();
  }

  close(): void {
    this._isOpen.set(false);
    this.cancelRedirect();
  }

  /**
   * Stops what the assistant has under way: the orchestrator turn (aborting its request) and any move
   * it was about to make to another screen. For the stop button and for every change of conversation.
   * Only the stop button passes `handBack`, to give the prompt back to the composer: on a change of
   * conversation that text would show up in the other conversation.
   */
  cancel(handBack = false): void {
    this.cancelRedirect();
    this.abandonRoute(handBack);
  }

  toggle(): void {
    if (this._isOpen()) this.close();
    else this.open();
  }

  hideBubble(): void {
    this.clearBubbleTimer();
    this._bubble.set(null);
  }

  send(text: string): void {
    const message = text.trim();
    if (!message || this.busy()) return;

    const screen = this.guideOnly() ? null : this.screens.screen();
    if (screen) this.sendToAgent(screen, message);
    else this.route(message);
  }

  /** Whether the composer's questionnaire in message `id` was answered already. */
  isQuestionnaireAnswered(id: string): boolean {
    return this.answeredQuestionnaires().has(id);
  }

  /**
   * Sends the answers to the composer's questionnaire in message `id` (WB-177 phase 5): the chat
   * shows what was picked as the user's turn, and the server composes the request it asked about
   * with them, without asking again. A questionnaire is answered once.
   */
  answerQuestionnaire(id: string, answers: ComposerAnswer[]): void {
    const asked = this.ai.messages().find((m) => m.id === id)?.composerQuestionnaire;
    if (!asked || this.isQuestionnaireAnswered(id) || this.busy()) return;
    const picked = answers.filter((a) => a.answer.length);
    const summary = picked.map((a) => `${a.question} ${a.answer.join(', ')}`).join('\n');
    // The server refuses an empty message: with nothing picked there is nothing to send.
    if (!summary.trim()) return;
    // Answered only once the turn ends well: a failed or stopped request leaves it open to retry.
    const markAnswered = () => this.answeredQuestionnaires.update((done) => new Set([...done, id]));
    // WB-222: the alerts agent asked it, so it gets the answers back as its own.
    const sent: QuestionnaireAnswers = { for: asked.clarifyFor === 'alert' ? 'alert' : 'dashboard', request: asked.request, answers: picked };
    this.route(summary, sent, markAnswered);
  }

  /** Where the card of the alert proposed in message `id` stands. */
  alertCard(id: string): AlertCardState {
    return this.alertCards()[id] ?? { status: 'pending' };
  }

  /**
   * Saves the alert of message `id` once the user confirmed it (WB-222). It is the only call that
   * writes: the server re-validates the proposal and creates everything or nothing. A card is
   * confirmed once; after a failure it can be tried again. The chat never navigates by itself: the
   * links of the applied card are the user's to press.
   */
  confirmAlert(id: string): void {
    const proposal = this.ai.messages().find((m) => m.id === id)?.alertProposal;
    const status = this.alertCard(id).status;
    if (!proposal || (status !== 'pending' && status !== 'error') || !this.canManageAlerts()) return;
    this.setAlertCard(id, { status: 'applying' });
    this.store
      .select(selectSelectedTenantId)
      .pipe(
        filter(isNotNull),
        take(1),
        switchMap((tenantId) => this.api.applyAlert(tenantId, proposal))
      )
      .subscribe({
        next: (result) => this.setAlertCard(id, { status: 'applied', result }),
        error: (error: unknown) => this.setAlertCard(id, alertFailure(error)),
      });
  }

  /** The user does not want the alert of message `id`: the card is closed and nothing is sent. */
  dismissAlert(id: string): void {
    const status = this.alertCard(id).status;
    if (status !== 'pending' && status !== 'error') return;
    this.setAlertCard(id, { status: 'dismissed' });
  }

  private setAlertCard(id: string, state: AlertCardState): void {
    this.alertCards.update((cards) => ({ ...cards, [id]: state }));
  }

  private route(message: string, questionnaire?: QuestionnaireAnswers, onAnswered?: () => void): void {
    const conversation = this.ai.conversationId();
    // A reply belongs to the conversation that asked for it: if another one is open by the time it
    // arrives, the turn is dropped instead of landing in it.
    const stale = () => {
      if (this.ai.conversationId() === conversation) return false;
      this.abandonRoute(false);
      return true;
    };
    this._cancelledMessage.set(null);
    const history = this.historyForOrchestrator();
    // Shown while the orchestrator works. The agent echoes the turn itself when it runs, so this local
    // copy is dropped then (see `apply`) and the message does not appear twice.
    const userTurn = this.local('user', message);
    this.ai.appendLocalMessages([userTurn]);
    this._routing.set(true);
    let answered = false;
    const active = { subscription: null as Subscription | null, userTurn, message, handBack: !questionnaire };
    this.activeRoute = active;

    const subscription = this.store
      .select(selectSelectedTenantId)
      .pipe(
        filter(isNotNull),
        take(1),
        switchMap((tenantId) => {
          const draft = this.openDraft();
          if (questionnaire?.for === 'alert') return this.api.routeMessageStream(tenantId, message, history, undefined, undefined, { request: questionnaire.request, answers: questionnaire.answers });
          if (questionnaire) return this.api.routeMessageStream(tenantId, message, history, draft, { request: questionnaire.request, answers: questionnaire.answers });
          return draft ? this.api.routeMessageStream(tenantId, message, history, draft) : this.api.routeMessageStream(tenantId, message, history);
        }),
        finalize(() => {
          if (this.activeRoute === active) this.activeRoute = null;
          this.clearRouteStream();
          this._routing.set(false);
        })
      )
      .subscribe({
        next: (event) => {
          if (stale()) return;
          if (event.type === 'done' || event.type === 'error') answered = true;
          if (event.type === 'done') onAnswered?.();
          this.onRouteEvent(event, userTurn);
        },
        error: () => {
          if (!stale()) this.reply(this.translation.translate('$ai.global.error'), { answeredBy: 'orchestrator' });
        },
        // A stream cut before its final event leaves no answer: say so rather than go quiet.
        complete: () => {
          if (!answered && !stale()) this.reply(this.translation.translate('$ai.global.error'), { answeredBy: 'orchestrator' });
        },
      });
    // A stream that already ended synchronously has no subscription left to keep.
    if (!subscription.closed) active.subscription = subscription;
  }

  /**
   * Unsubscribes from the orchestrator turn in flight, which aborts its request, and takes its local
   * echo out of the conversation. A turn the user stopped gives its prompt back (`handBack`).
   */
  private abandonRoute(handBack = true): void {
    const active = this.activeRoute;
    if (!active) return;
    this.activeRoute = null;
    active.subscription?.unsubscribe();
    this.ai.removeMessage(active.userTurn.id);
    if (handBack && active.handBack) this._cancelledMessage.set(active.message);
    this.clearRouteStream();
    this._routing.set(false);
  }

  private onRouteEvent(event: RouteStreamEvent, userTurn: ChatMessage): void {
    switch (event.type) {
      case 'step':
        this._routeStep.set(event);
        return;
      case 'token':
        this._streamingReply.update((text) => text + event.delta);
        return;
      case 'reset':
        this._streamingReply.set('');
        return;
      case 'done':
        // The final reply replaces what was streamed: it is the same text, already cleaned up.
        this.clearRouteStream();
        this.apply(event.result, userTurn);
        return;
      case 'error':
        this.clearRouteStream();
        this.reply(this.translation.translate('$ai.global.error'), { answeredBy: 'orchestrator' });
        return;
    }
  }

  private clearRouteStream(): void {
    this._streamingReply.set('');
    this._routeStep.set(null);
  }

  private apply(result: OrchestratorResult, userTurn: ChatMessage): void {
    const links = result.links.length ? { guideLinks: result.links } : {};
    if (result.alertProposal) {
      // WB-222: the alert is shown as a card to confirm; nothing is saved and the chat stays where it is.
      this.reply(result.reply, { ...links, answeredBy: 'alerts', alertProposal: result.alertProposal });
      return;
    }
    if (result.clarify) {
      // WB-177 phase 5: the composer asks before assuming anything; the chat shows its questionnaire.
      // WB-222: the alerts agent asks the same way, and its answers go back to it.
      const clarifyFor = result.clarifyFor ?? 'dashboard';
      const composerQuestionnaire = { request: result.clarifyRequest ?? userTurn.content, clarify: result.clarify, clarifyFor };
      this.reply(result.reply, { ...links, answeredBy: clarifyFor === 'alert' ? 'alerts' : 'composer', composerQuestionnaire });
      return;
    }
    if (result.dashboardProposals?.length && result.revisedProposal) {
      // WB-179: the editor is already open with the proposal; the revision is laid over it (WB-207) and
      // the panel stays, so the user can go on correcting it.
      this.reply(result.reply, { ...links, answeredBy: 'composer' });
      if (this.canCreateDashboards()) this.dashboardProposals.offerRevision(result.dashboardProposals[0]);
      return;
    }
    if (result.dashboardProposals?.length) {
      const said = this.reply(result.reply, { ...links, answeredBy: 'composer' });
      // The proposal is not shown in the chat: the editor is filled with it, where it can be seen
      // whole — so the panel steps aside, or it would cover the form and its proposal buttons.
      if (this.canCreateDashboards()) {
        this.dashboardProposals.offer(result.dashboardProposals);
        this.redirectAfter(said, () => {
          this.close();
          void this.router.navigate([DASHBOARD_EDITOR_PATH]);
        });
      }
      return;
    }
    if (!result.handoff) {
      const byGuide = result.steps.some((s) => s.tool === 'find_in_dashboards' && s.outcome === 'ok');
      // WB-222: the alerts agent's own answer without a proposal (it cannot do what was asked) wears its face.
      const byAlerts = !byGuide && result.steps.some((s) => s.tool === 'manage_alerts');
      // The guide takes the user to the place instead of offering a link to it: the first link is
      // the best match, so that one is opened and only the others stay in the chat as alternatives.
      const [best, ...others] = result.links;
      const said = this.reply(result.reply, { ...(others.length ? { guideLinks: others } : {}), answeredBy: byGuide ? 'guide' : byAlerts ? 'alerts' : 'orchestrator' });
      if (best) this.redirectAfter(said, () => this.openGuideLink(best));
      return;
    }

    if (this.guideOnly()) {
      this.reply(this.translation.translate('$ai.global.guide-only'), { ...links, answeredBy: 'guide' });
      return;
    }

    const { agent, request } = result.handoff;
    this.ai.removeMessage(userTurn.id);
    // What the guide found on the way stays in the conversation, next to where the user is taken.
    const goingTo: ChatMessage = { ...this.local('assistant', this.translation.translate(GOING_TO[agent])), ...links, answeredBy: 'orchestrator' };
    this.ai.appendLocalMessages([goingTo]);
    // The turn is sent from the screen once it is up: it carries that screen's config.
    this.screens.whenScreen(agent, (screen) => this.sendToAgent(screen, request));
    this.redirectAfter(goingTo, () => void this.router.navigate([SCREEN_PATH[agent]]));
  }

  /**
   * The chat shows no generated config: every result goes into a form, which previews it. On the
   * screen that can apply it, it is applied at once; the agents can produce either result wherever
   * they run (asked for a chart on the query screen, the query agent builds one), and a result the
   * open screen cannot apply is taken to the screen that can. On the chart builder a proposed query
   * opens in its dialog, where it is validated and saved: it is how that screen gets the chart's
   * feeding query.
   */
  private takeResultToItsScreen(turn: ChatMessage[]): void {
    if (this.guideOnly()) return;
    const chart = [...turn].reverse().find((m) => m.chartConfig);
    const query = [...turn].reverse().find((m) => m.queryConfig);
    const screen = this.screens.screen();
    const here = screen?.mode ?? null;

    if (chart?.chartConfig && this.canCreate.chart()) {
      const event = { chartConfig: chart.chartConfig, savedQuery: chart.savedQuery, mode: chart.chartConfigMode };
      if (here === 'chart') screen?.applyChartConfig?.(event);
      else this.goApply('chart', (target) => target.applyChartConfig?.(event));
    } else if (!chart && query?.queryConfig) {
      // A query the server would refuse to save is not offered; say why instead of going quiet.
      if (!this.canCreate.query()) {
        this.reply(this.translation.translate('$ai.global.no-query-permission'), { answeredBy: 'orchestrator' });
        return;
      }
      const queryConfig = query.queryConfig;
      if (here === 'query') screen?.applyQueryConfig?.(queryConfig);
      else if (here === 'chart') screen?.openQueryConfigDialog?.(queryConfig);
      else this.goApply('query', (target) => target.applyQueryConfig?.(queryConfig));
    }
  }

  private goApply(mode: AssistantAgentMode, apply: (screen: AssistantScreen) => void): void {
    const goingTo: ChatMessage = { ...this.local('assistant', this.translation.translate(`$ai.global.going-to.apply_${mode}`)), answeredBy: 'orchestrator' };
    this.ai.appendLocalMessages([goingTo]);
    this.screens.whenScreen(mode, apply);
    this.redirectAfter(goingTo, () => void this.router.navigate([SCREEN_PATH[mode]]));
  }

  /**
   * Change screens on the assistant's own initiative, once the user has had time to read the
   * message that announces it; the panel marks that message meanwhile.
   */
  private redirectAfter(message: ChatMessage, go: () => void): void {
    this.clearRedirectTimer();
    this._redirecting.set(message.id);
    this.redirectTimer = setTimeout(() => {
      this.redirectTimer = null;
      this._redirecting.set(null);
      go();
    }, REDIRECT_MS);
  }

  private clearRedirectTimer(): void {
    if (this.redirectTimer) clearTimeout(this.redirectTimer);
    this.redirectTimer = null;
  }

  /** Drops the move to another screen the assistant was about to make, and what it was to do there. */
  private cancelRedirect(): void {
    this.clearRedirectTimer();
    this._redirecting.set(null);
    this.screens.cancelPending();
  }

  /** Open the dashboard a guide answer points to, scrolled to its block when it names one. */
  openGuideLink(link: GuideLink): void {
    // The user chose where to go: whatever the assistant had planned instead is off.
    this.cancelRedirect();
    void this.router.navigate(['/dashboard', link.dashboardId], { queryParams: link.blockId ? { block: link.blockId } : {} });
  }

  private reply(text: string, extra: Partial<ChatMessage> = {}): ChatMessage {
    const message = { ...this.local('assistant', text), ...extra };
    this.ai.appendLocalMessages([message]);
    this.notifyIfClosed();
    return message;
  }

  private sendToAgent(screen: AssistantScreen, message: string): void {
    const config = screen.currentConfig() ?? undefined;
    this.ai.sendMessage(
      message,
      screen.mode === 'query' ? config : undefined,
      screen.mode === 'chart' ? config : undefined,
      screen.mode
    );
  }

  /** The proposal open in the editor as the form holds it, for the assistant to change (WB-179); only for who may create dashboards. */
  private openDraft(): ProposalRevisionDraft | undefined {
    return (this.canCreateDashboards() && this.dashboardProposals.takeRevisionDraft()) || undefined;
  }

  /** The visible conversation, as the orchestrator reads it: no tool steps, capped like the server. */
  private historyForOrchestrator(): RouteHistoryTurn[] {
    return this.ai
      .messages()
      .filter((m) => (m.role === 'user' || m.role === 'assistant') && !m.toolCall && !!m.content)
      .slice(-HISTORY_SENT)
      .map((m) => ({ role: m.role as RouteHistoryTurn['role'], content: m.content.slice(0, HISTORY_TURN_CHARS) }));
  }

  private notifyIfClosed(): void {
    if (this._isOpen()) return;
    const last = [...this.ai.messages()].reverse().find((m) => m.role === 'assistant' && !m.toolCall && !!m.content);
    const text = this.ai.failedMessage() ? this.translation.translate('$ai.global.failed') : last ? preview(last.content) : null;
    if (!text) return;

    this._unread.set(true);
    this._bubble.set(text);
    this.clearBubbleTimer();
    this.bubbleTimer = setTimeout(() => this._bubble.set(null), BUBBLE_MS);
  }

  private clearBubbleTimer(): void {
    if (this.bubbleTimer) clearTimeout(this.bubbleTimer);
    this.bubbleTimer = null;
  }

  private local(role: 'user' | 'assistant', content: string): ChatMessage {
    return { id: crypto.randomUUID(), role, content, createdAt: new Date().toISOString() };
  }
}

/** First words of a markdown reply as plain text, cut on a word boundary. */
function preview(markdown: string): string {
  const plain = markdown
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/[*_`#>|~]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (plain.length <= BUBBLE_CHARS) return plain;
  const cut = plain.slice(0, BUBBLE_CHARS);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > BUBBLE_CHARS / 2 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}

/** The answers to a questionnaire, with the request they answer and who asked it (decides the field they travel in). */
interface QuestionnaireAnswers extends ComposerAnswers {
  for: 'dashboard' | 'alert';
}

/** The `code`s of a refused apply the card has its own text for (`$ai.alerts.error.<code>`). */
const ALERT_ERROR_CODES: ReadonlySet<string> = new Set<AlertApplyErrorCode>([
  'invalid_proposal',
  'no_actions',
  'recipient_not_active',
  'action_not_found',
  'query_forbidden',
  'query_create_forbidden',
  'not_found',
  'stale',
  'forbidden',
]);

/**
 * What a failed `applyAlert` means for the card. The server's `code` is read first: the query
 * refusals come as 403 but are errors with their own text; only `code: 'forbidden'`, or a 403
 * without a known code, is the «no permission» state. The server's `message` is English and never shown.
 */
function alertFailure(error: unknown): AlertCardState {
  const status = error instanceof HttpErrorResponse ? error.status : -1;
  if (status === 0) return { status: 'error', error: { key: 'network' } };
  const code = error instanceof HttpErrorResponse ? (error.error as { code?: unknown } | null)?.code : undefined;
  // `forbidden` is the state below, not an error line.
  if (typeof code === 'string' && code !== 'forbidden' && ALERT_ERROR_CODES.has(code)) return { status: 'error', error: { key: code as AlertErrorKey } };
  if (status === 403) return { status: 'forbidden' };
  // A missing alert (404) and a conflict (409) both mean the alert changed since the proposal.
  if (status === 404 || status === 409) return { status: 'error', error: { key: 'stale' } };
  return { status: 'error', error: { key: 'generic' } };
}
